/**
 * Enforces two White Rose sizing rules on the product page:
 *  1. A standard Size must be explicitly chosen (Shopify's own default
 *     first-available-variant preselection is cleared on load) before
 *     Add to Cart / accelerated checkout can be used.
 *  2. The custom-size panel, once opened, must be fully completed (or
 *     closed again) before Add to Cart can be used — it can't be left
 *     half-filled.
 *
 * Plain, framework-independent, event-delegated script (no custom element
 * registration) so it keeps working across the theme's own morph-based
 * re-renders of the variant picker.
 */
(function () {
  function sizeFieldsetFor(productId) {
    var picker = document.querySelector('variant-picker[data-product-id="' + productId + '"]');
    return picker ? picker.querySelector('fieldset[data-option-name="Size"]') : null;
  }

  function isStandardSizeChosen(productId) {
    var fieldset = sizeFieldsetFor(productId);
    if (!fieldset) return true;
    return !!fieldset.querySelector('input[type="radio"]:checked');
  }

  function panelFor(productId) {
    return document.querySelector('custom-size-panel[data-product-id="' + productId + '"]');
  }

  function isCustomSizingSatisfied(productId) {
    var panel = panelFor(productId);
    if (!panel) return true;
    var details = panel.querySelector('details');
    if (!details || !details.open) return true;
    var inputs = panel.querySelectorAll('input[data-measure]');
    for (var i = 0; i < inputs.length; i++) {
      var numericValue = parseFloat(inputs[i].value);
      if (inputs[i].value.trim() === '' || isNaN(numericValue) || numericValue <= 0) return false;
    }
    return true;
  }

  function setAddToCartAvailability(productId, allowed) {
    document
      .querySelectorAll('product-form-component[data-product-id="' + productId + '"] add-to-cart-component')
      .forEach(function (component) {
        if (typeof component.disable === 'function' && typeof component.enable === 'function') {
          allowed ? component.enable() : component.disable();
        }
      });

    document
      .querySelectorAll(
        'product-form-component[data-product-id="' + productId + '"] [ref="acceleratedCheckoutButtonContainer"]'
      )
      .forEach(function (container) {
        allowed ? container.removeAttribute('hidden') : container.setAttribute('hidden', 'true');
      });

    document
      .querySelectorAll('sticky-add-to-cart[data-product-id="' + productId + '"] [ref="addToCartButton"]')
      .forEach(function (button) {
        button.disabled = !allowed;
      });
  }

  function refreshGate(productId) {
    if (!productId) return;
    var allowed = isStandardSizeChosen(productId) && isCustomSizingSatisfied(productId);
    setAddToCartAvailability(productId, allowed);

    var panel = panelFor(productId);
    if (!panel) return;
    var hint = panel.querySelector('[data-role="hint"]');
    if (!hint) return;
    var details = panel.querySelector('details');
    var incomplete = !!(details && details.open && !isCustomSizingSatisfied(productId));
    hint.hidden = !incomplete;
  }

  function clearDefaultSizePreselection() {
    document.querySelectorAll('fieldset[data-option-name="Size"] input[type="radio"]:checked').forEach(function (radio) {
      radio.checked = false;
      radio.removeAttribute('checked');
      radio.dataset.currentChecked = 'false';
    });
  }

  /**
   * The theme's own sliding selection "pill" (.variant-option__button-label__pill)
   * is unconditionally rendered at full size with the selected-state background,
   * relying on Shopify's native first-variant preselection always leaving exactly
   * one radio checked. clearDefaultSizePreselection() intentionally leaves every
   * Size radio unchecked (so nothing is purchasable until a real choice is made),
   * which put every swatch into that "selected" pill appearance at once, hiding
   * every size label behind solid black. This adds one scoped, JS-driven CSS rule
   * so the pill only shows once a real selection exists — the same visual result
   * as every other unselected swatch already gets after a size IS chosen.
   */
  function fixUnselectedPillVisibility() {
    if (document.getElementById('size-guard-pill-fix')) return;
    var style = document.createElement('style');
    style.id = 'size-guard-pill-fix';
    style.textContent =
      'fieldset[data-option-name="Size"]:not(:has(input:checked)) .variant-option__button-label__pill' +
      ' { opacity: 0; }';
    document.head.appendChild(style);
  }

  function convertMeasurement(value, fromUnit, toUnit) {
    if (value === '' || Number.isNaN(Number(value))) return '';
    if (fromUnit === toUnit) return value;
    var inches = fromUnit === 'cm' ? Number(value) / 2.54 : Number(value);
    var result = toUnit === 'cm' ? inches * 2.54 : inches;
    return Math.round(result * 10) / 10;
  }

  function setPanelUnit(panel, unit) {
    var currentUnit = panel.dataset.unit || 'in';
    if (currentUnit === unit) return;

    panel.querySelectorAll('input[data-measure]').forEach(function (input) {
      var converted = convertMeasurement(input.value, currentUnit, unit);
      input.value = converted === '' ? '' : converted;
      input.name = 'properties[Custom ' + input.dataset.label + ' (' + unit + ')]';
    });

    panel.querySelectorAll('[data-role="unit-suffix"]').forEach(function (el) {
      el.textContent = unit;
    });

    panel.querySelectorAll('.custom-size-panel__unit-btn').forEach(function (btn) {
      var isActive = btn.dataset.unit === unit;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });

    panel.dataset.unit = unit;
  }

  function setPanelExpanded(panel, expanded) {
    panel.querySelectorAll('input[data-measure]').forEach(function (input) {
      input.disabled = !expanded;
      input.required = expanded;
    });

    var sizingFlag = panel.querySelector('[data-role="sizing-flag"]');
    if (sizingFlag) sizingFlag.disabled = !expanded;
  }

  document.addEventListener('DOMContentLoaded', function () {
    fixUnselectedPillVisibility();
    clearDefaultSizePreselection();

    document.querySelectorAll('variant-picker[data-product-id]').forEach(function (picker) {
      refreshGate(picker.dataset.productId);
    });

    document.querySelectorAll('custom-size-panel[data-product-id]').forEach(function (panel) {
      panel.dataset.unit = 'in';
      refreshGate(panel.dataset.productId);
    });
  });

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var fieldset = target.closest('fieldset[data-option-name="Size"]');
    if (fieldset) {
      var picker = target.closest('variant-picker[data-product-id]');
      if (picker) refreshGate(picker.dataset.productId);
    }

    var measureInput = target.closest('input[data-measure]');
    if (measureInput) {
      var panelFromInput = measureInput.closest('custom-size-panel[data-product-id]');
      if (panelFromInput) refreshGate(panelFromInput.dataset.productId);
    }
  });

  document.addEventListener('input', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var measureInput = target.closest('input[data-measure]');
    if (measureInput) {
      var panel = measureInput.closest('custom-size-panel[data-product-id]');
      if (panel) refreshGate(panel.dataset.productId);
    }
  });

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var unitButton = target.closest('.custom-size-panel__unit-btn');
    if (unitButton) {
      var panel = unitButton.closest('custom-size-panel[data-product-id]');
      if (panel) setPanelUnit(panel, unitButton.dataset.unit);
    }
  });

  document.addEventListener(
    'toggle',
    function (event) {
      var details = event.target;
      if (!(details instanceof HTMLDetailsElement) || typeof details.closest !== 'function') return;
      var panel = details.closest('custom-size-panel[data-product-id]');
      if (!panel) return;
      setPanelExpanded(panel, details.open);
      refreshGate(panel.dataset.productId);
    },
    true
  );
})();
